/**
 * Case intake wizard (office staff). Three steps: beneficiary (with live dedupe check),
 * documents (multipart upload to /uploads), then case details + review. On submit it
 * creates the beneficiary, then the case — which re-runs dedupe and records flags.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Upload, AlertTriangle, UserPlus, FileText, ClipboardCheck,
} from 'lucide-react';
import { api } from '../../lib/apiClient.js';
import { useToast } from '../../components/feedback/ToastProvider.jsx';
import { Field, TextInput, Select } from '../../components/ui/Field.jsx';
import { CATEGORY_META, formatINR } from '../../features/cases/caseMeta.js';
import styles from './CaseIntake.module.css';

const STEPS = [
  { key: 'beneficiary', label: 'Beneficiary', icon: UserPlus },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'case', label: 'Case & review', icon: ClipboardCheck },
];

const DOC_FIELDS = [
  { key: 'aadhaarDoc', label: 'Aadhaar' },
  { key: 'casteCertDoc', label: 'Caste certificate' },
  { key: 'incomeProofDoc', label: 'Income proof' },
  { key: 'photo', label: 'Beneficiary photo' },
];

export default function CaseIntake() {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [dupFlags, setDupFlags] = useState([]);
  const [ben, setBen] = useState({
    name: '', phone: '', aadhaar: '', address: '', village: '', block: '',
    consentPublic: true, consentTestimonial: false,
  });
  const [docs, setDocs] = useState({});
  const [kase, setKase] = useState({ category: 'ration', amountRequested: '', summary: '', publicSummary: '' });
  const [errors, setErrors] = useState({});

  const setB = (k) => (e) => setBen((s) => ({ ...s, [k]: e.target.value }));
  const setK = (k) => (e) => setKase((s) => ({ ...s, [k]: e.target.value }));

  const runDedupe = useCallback(async () => {
    if (!/^[+]?[0-9]{10,15}$/.test(ben.phone) && ben.aadhaar.length !== 12) return;
    try {
      const params = new URLSearchParams();
      if (ben.phone) params.set('phone', ben.phone);
      if (ben.aadhaar.length === 12) params.set('aadhaar', ben.aadhaar);
      const res = await api.get(`/beneficiaries/dedupe-check?${params}`);
      setDupFlags(res.flags || []);
    } catch {
      /* dedupe is advisory; ignore errors */
    }
  }, [ben.phone, ben.aadhaar]);

  const validateBeneficiary = () => {
    const e = {};
    if (ben.name.trim().length < 2) e.name = 'Enter a name';
    if (!/^[+]?[0-9]{10,15}$/.test(ben.phone.trim())) e.phone = 'Valid phone required';
    if (ben.aadhaar && !/^[0-9]{12}$/.test(ben.aadhaar)) e.aadhaar = 'Aadhaar is 12 digits';
    if (!ben.village.trim()) e.village = 'Village required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const uploadDoc = async (key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('files', file);
    try {
      const res = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocs((d) => ({ ...d, [key]: res.url }));
      toast.success(`${file.name} uploaded.`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const flow = kase.category === 'housing' || Number(kase.amountRequested) >= 25000 ? 'heavy' : 'light';

  const submit = async () => {
    const e = {};
    if (!(Number(kase.amountRequested) > 0)) e.amountRequested = 'Enter an amount';
    if (!kase.publicSummary.trim()) e.publicSummary = 'A public (PII-free) summary is required';
    setErrors(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    try {
      // 1. Create the beneficiary (with uploaded doc URLs).
      const beneficiary = await api.post('/beneficiaries', {
        name: ben.name.trim(),
        phone: ben.phone.trim(),
        aadhaar: ben.aadhaar || undefined,
        address: ben.address,
        village: ben.village,
        block: ben.block,
        consent: { publicAfterClose: ben.consentPublic, testimonial: ben.consentTestimonial },
        ...docs,
      });
      // 2. Create the case (re-runs dedupe + records flags).
      const created = await api.post('/cases', {
        beneficiary: beneficiary.id,
        category: kase.category,
        flow,
        amountRequested: Number(kase.amountRequested),
        summary: kase.summary,
        publicSummary: kase.publicSummary,
      });
      toast.success(`Case ${created.caseNo} created.`);
      navigate(`/cases/${created.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step === 0 && !validateBeneficiary()) return;
    if (step === 0) runDedupe();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/cases')}>
        <ArrowLeft size={16} /> Cancel intake
      </button>
      <h1 className={styles.h1}>New case intake</h1>

      <ol className={styles.steps}>
        {STEPS.map((s, i) => (
          <li key={s.key} className={`${styles.stepItem} ${i === step ? styles.current : ''} ${i < step ? styles.done : ''}`}>
            <span className={styles.stepIcon}>{i < step ? <Check size={16} /> : <s.icon size={16} />}</span>
            {s.label}
          </li>
        ))}
      </ol>

      <div className={styles.panel}>
        {step === 0 && (
          <div className={styles.formGrid}>
            <Field label="Full name" required error={errors.name}>
              <TextInput value={ben.name} onChange={setB('name')} placeholder="Gurpreet Kaur" />
            </Field>
            <Field label="Phone" required error={errors.phone}>
              <TextInput value={ben.phone} onChange={setB('phone')} onBlur={runDedupe} placeholder="98xxxxxxxx" inputMode="numeric" />
            </Field>
            <Field label="Aadhaar (12 digits)" error={errors.aadhaar} hint="Stored only as a salted hash">
              <TextInput value={ben.aadhaar} onChange={setB('aadhaar')} onBlur={runDedupe} maxLength={12} inputMode="numeric" />
            </Field>
            <Field label="Village" required error={errors.village}>
              <TextInput value={ben.village} onChange={setB('village')} />
            </Field>
            <Field label="Block">
              <TextInput value={ben.block} onChange={setB('block')} />
            </Field>
            <Field label="Address">
              <TextInput value={ben.address} onChange={setB('address')} />
            </Field>
            <div className={styles.full}>
              <label className={styles.check}>
                <input type="checkbox" checked={ben.consentPublic} onChange={(e) => setBen((s) => ({ ...s, consentPublic: e.target.checked }))} />
                Consents to public listing after the case closes (anonymous during)
              </label>
              <label className={styles.check}>
                <input type="checkbox" checked={ben.consentTestimonial} onChange={(e) => setBen((s) => ({ ...s, consentTestimonial: e.target.checked }))} />
                Consents to share a testimonial
              </label>
            </div>
            {dupFlags.length > 0 && (
              <div className={`${styles.full} ${styles.dupWarn}`}>
                <AlertTriangle size={18} />
                <span>
                  {dupFlags.length} possible duplicate(s) flagged ({dupFlags.map((f) => f.type).join(', ')}). The
                  panel will review — intake can still proceed.
                </span>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className={styles.docGrid}>
            {DOC_FIELDS.map((d) => (
              <div key={d.key} className={styles.docUpload}>
                <span className={styles.docLabel}>{d.label}</span>
                {docs[d.key] ? (
                  <div className={styles.docDone}>
                    <Check size={16} /> Uploaded
                    <button onClick={() => setDocs((s) => ({ ...s, [d.key]: undefined }))}>Replace</button>
                  </div>
                ) : (
                  <label className={styles.dropzone}>
                    <Upload size={20} />
                    <span>Click to upload</span>
                    <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => uploadDoc(d.key, e.target.files[0])} />
                  </label>
                )}
              </div>
            ))}
            <p className={styles.docNote}>Images or PDF, up to 12 MB each. Optional — they can be added later.</p>
          </div>
        )}

        {step === 2 && (
          <div className={styles.formGrid}>
            <Field label="Category" required>
              <Select value={kase.category} onChange={setK('category')}>
                {Object.entries(CATEGORY_META).map(([k, m]) => (
                  <option key={k} value={k}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Amount requested (₹)" required error={errors.amountRequested}>
              <TextInput type="number" min="0" value={kase.amountRequested} onChange={setK('amountRequested')} />
            </Field>
            <div className={styles.full}>
              <div className={styles.flowNote}>
                Verification flow: <strong>{flow === 'heavy' ? 'Heavy (mandatory field verification)' : 'Light (ration)'}</strong>
                {flow === 'heavy' && ' — housing or ≥ ₹25,000'}
              </div>
            </div>
            <Field label="Internal summary">
              <TextInput value={kase.summary} onChange={setK('summary')} placeholder="Notes for staff (not public)" />
            </Field>
            <Field label="Public summary (PII-free)" required error={errors.publicSummary}>
              <TextInput value={kase.publicSummary} onChange={setK('publicSummary')} placeholder="What appears on the Served wall" />
            </Field>
            <div className={`${styles.full} ${styles.review}`}>
              <h3>Review</h3>
              <p>
                <strong>{ben.name}</strong> · {ben.village} · {CATEGORY_META[kase.category].label} ·{' '}
                {formatINR(Number(kase.amountRequested) || 0)} · {flow} flow
              </p>
            </div>
          </div>
        )}
      </div>

      <div className={styles.nav}>
        {step > 0 && (
          <button className={styles.ghost} onClick={() => setStep((s) => s - 1)} disabled={busy}>
            <ArrowLeft size={16} /> Back
          </button>
        )}
        <div className={styles.spacer} />
        {step < STEPS.length - 1 ? (
          <button className={styles.primary} onClick={next}>
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button className={styles.primary} onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create case'} <Check size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
